<template>
  <LeftNav :enabled-nav="enabledAdminNavs" />
</template>

<script lang="js">
import {useAppCoreStore} from "@/src/stores/app-core";
import {PermissionNames} from "@/src/enums";
import LeftNav from "@/src/components/shared/LeftNav.vue";

/**
 * This array controls the items that appear in the left nav.
 * Array contains Objects for each section. 
 * {
 *    name: 'Title of the Section', // required
 *    subsections: [ // required. The actual Links to display in the subsection. Each link is an object
 *      {
 *        name: 'Text of the link', // required.
 *        url_path: 'string_name_of_the_vue_router_path', // required.
 *        permission_key: PermissionNames.XXX_YYYY // required. The permission to check if the user has it or not, use PermissionNames enum
 *        check_feature: (settings) > { return BOOLEAN BASED ON FEATURE CHECK } // Optional function to check if a feature should be checked before enabling
 *                                                                              // This link. appStore.applicationSettings are passed to the function.  
 *      }
 *    ]
 *  
 * }
 */
const AdminAreaNavs = [
  {
    name: 'User, Teams, TxC and Roles',
    sub_sections: [
      {
        name: 'Manage Membership Categories',
        url_path: 'membership_categories',
        permission_key: PermissionNames.ADMIN_MEMBERSHIP_CATEGORIES,
      },
      {
        name: 'Manage Teams',
        url_path: 'admin_teams',
        permission_key: PermissionNames.ADMIN_TEAMS,
      },
      {
        name: 'Manage TXC',
        url_path: 'admin_transplant_centers',
        permission_key: PermissionNames.ADMIN_TRANSPLANT_CENTER_CONFIGURATIONS,
      },
      {
        name: 'Manage Roles',
        url_path: 'admin_roles',
        permission_key: PermissionNames.ADMIN_ROLES,
      },
      {
        name: 'Manage Users',
        url_path: 'admin_users',
        permission_key: PermissionNames.ADMIN_USERS,
      },
      {
        name: 'Import Users',
        url_path: 'admin_mass_import_users',
        permission_key: PermissionNames.ADMIN_USERS_MASS_IMPORT,
      }
      
    ],
  },
  {
    name: 'Manage Dashboards',
    sub_sections: [
      {
        name: 'Super Team Dashboards',
        url_path: 'admin_super_team_dashboards',
        permission_key: PermissionNames.ADMIN_DASHBOARD_CONFIGURATIONS_INTERNAL_SUPER_TEAM,
      }
    
    ]
  },
  {
    name: 'Comms Templates',
    sub_sections: [
      {
        name: 'Manage Template Categories',
        url_path: 'admin_communication_template_categories',
        permission_key: PermissionNames.ADMIN_COMMUNICATION_TEMPLATES,
      },
      {
        name: 'Manage Templates',
        url_path: 'admin_communication_templates',
        permission_key: PermissionNames.ADMIN_COMMUNICATION_TEMPLATES,
      },
    ],
  },
  {
    name: 'Checklist',
    sub_sections: [
      {
        name: 'Manage Checklist Template Categories',
        url_path: 'admin_checklist_template_categories',
        permission_key: PermissionNames.ADMIN_MATCH_CHECKLISTS,
      },
      {
        name: 'Manage Checklist Item Types',
        url_path: 'admin_checklist_item_types',
        permission_key: PermissionNames.ADMIN_MATCH_CHECKLISTS,
      },
      {
        name: 'Manage Checklist Templates',
        url_path: 'admin_checklist_templates',
        permission_key: PermissionNames.ADMIN_MATCH_CHECKLISTS,
      },
    ],
  },
  {
    name: 'Manage Tags',
     sub_sections: [
      {
        name: 'Manage Match Tag Categories',
        url_path: 'admin_tag_categories',
        permission_key: PermissionNames.ADMIN_MATCH_TAGS,
      }
    ]
  },
  {
    name: 'Auto rule outs',
    sub_sections: [
      {
        name: 'Manage Auto rule outs',
        url_path: 'admin_aro_rules',
        permission_key: PermissionNames.ADMIN_ARO_RULES,
      },
      {
        name: 'Manage Procedures',
        url_path: 'admin_procedure_notes',
        permission_key: PermissionNames.ADMIN_PROCEDURE_NOTES,
      }
    ],
  },
  {
    name: 'Troubleshoot',
    sub_sections: [
      {
        name: 'Donors',
        url_path: 'admin_troubleshoot_donors',
        permission_key: PermissionNames.ADMIN_TROUBLESHOOT_DONORS,
      },
      {
        name: 'Matches',
        url_path: 'admin_troubleshoot_matches',
        permission_key: PermissionNames.ADMIN_TROUBLESHOOT_MATCHES
      },
      {
        name: 'Robocall Reduction',
        url_path: 'admin_troubleshoot_robocall_reduction',
        permission_key: PermissionNames.ADMIN_TROUBLESHOOT_ROBOCALL_REDUCTION
      }    ],
  },
  {
    name: 'UNOS Finalized Offer CSVs',
    sub_sections: [
      {
        name: 'Finalized Offer CSV Batches',
        url_path: 'admin_unos_finalized_offers_csv_batch',
        permission_key: PermissionNames.ADMIN_UNOS_FINALIZED_OFFERS_CSV_BATCH,
      },
      {
        name: 'Mask CSV Zip',
        url_path: 'admin_unos_mask_csv',
        permission_key: PermissionNames.ADMIN_UNOS_MASK_CSV,
      }
    ]
  },
  {
    name: 'Integrations',
    sub_sections: [
      {
        name: 'OmniLife Workflow Mappings',
        url_path: 'admin_integrations_omnilife_workflow_mappings',
        permission_key: PermissionNames.ADMIN_INTEGRATIONS_OMNILIFE_WORKFLOWS,
        check_feature: (settings) => { return settings && settings.integrations && settings.integrations.omnilife_workflows }
      },
      {
        name: 'OmniLife User Mappings',
        url_path: 'admin_integrations_omnilife_user_mappings',
        permission_key: PermissionNames.ADMIN_INTEGRATIONS_OMNILIFE_WORKFLOWS,
        check_feature: (settings) => { return settings && settings.integrations && settings.integrations.omnilife_workflows }
      },
      {
        name: 'Email Notifications',
        url_path: 'admin_integrations_email_notifications',
        permission_key: PermissionNames.ADMIN_INTEGRATIONS_EMAIL_NOTIFICATIONS,
      }
    ],
  },
  {
    name: 'Application Settings',
    sub_sections: [
      {
        name: 'Manage Robocall Reduction',
        url_path: 'admin_configurations_robocall_reduction',
        permission_key: PermissionNames.ADMIN_CONFIGURATIONS_ROBOCALL_REDUCTION,
      }
    ]
  },
  {
    name: 'Core Lookups',
    sub_sections: [
      {
        name: 'Manage Organs',
        url_path: 'admin_organs',
        permission_key: PermissionNames.ADMIN_ORGAN_AND_ORGAN_GROUPS,
      },
      {
        name: 'Manage Organ Groups',
        url_path: 'admin_organ_groups',
        permission_key: PermissionNames.ADMIN_ORGAN_AND_ORGAN_GROUPS,
      },
    ]
  }
]
export default {
  components: {
    LeftNav
  },
  setup() {
    const appStore = useAppCoreStore();
    return {
      appStore
    }
  },
  computed: {
    user() {
      return this.appStore.currentUser;
    },
    userPermissions() {
      return this.appStore.currentUser ? this.appStore.currentUser.userPermissions : null;
    },
    enabledAdminNavs() {
      if(!this.user || !this.userPermissions) return [];
       return AdminAreaNavs.map((admin_nav) => {
        admin_nav.sub_sections.map((sub) =>{
          const nav_perm = this.userPermissions[sub.permission_key];
          sub.enabled = nav_perm ? (nav_perm.read || false) : false;
          if(sub.check_feature) {
            // Have permission, but feature disabled, then it will be disabled.
            sub.enabled = sub.check_feature(this.appStore.applicationSettings) && sub.enabled ? true : false;
          }
          return sub;
        })
        admin_nav.enabled = admin_nav.sub_sections.some(sub => sub.enabled);
        return admin_nav;
      })
    },
  }
}
</script>
